import { gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback } from 'react';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { useUnassignConfigurationMutation } from './__generated__/UnAssignConfigurationModal.graphql';

interface Props {
  configurationId?: string;
  displayId: string;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
}

export function UnAssignConfigurationModal({
  configurationId,
  displayId,
  isOpen,
  onCancel,
}: Props) {
  const toast = useToast();
  const [unAssignConfiguration, { loading }] = useUnassignConfigurationMutation();

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleUnassign = useCallback(async () => {
    try {
      if (configurationId) {
        await unAssignConfiguration({
          variables: {
            input: {
              id: configurationId,
              displayIds: [displayId],
            },
          },
          refetchQueries: ['CustomerDisplaysIndexPage'],
          awaitRefetchQueries: true,
        });
        toast({
          status: 'success',
          title: t('configUnassignSuccess'),
        });
      }
    } catch (err) {
      toast({
        status: 'error',
        title: t('configUnassignError'),
      });
    } finally {
      onCancel();
    }
  }, [unAssignConfiguration, configurationId, displayId, toast, onCancel]);

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('unassignConfigurationHeader')}</ModalHeader>
        <ModalCloseButton tabIndex={5} />
        <ModalBody>
          <Text>{t('unassignConfigurationConfirmation')}</Text>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} variant="ghost" colorScheme="blue" isDisabled={loading}>
            {t('cancel')}
          </Button>
          <Button
            variant="solid"
            colorScheme="red"
            marginLeft="3"
            onClick={handleUnassign}
            isDisabled={loading}
          >
            {t('unassign')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

UnAssignConfigurationModal.graphql = {
  mutations: {
    BulkUnAssignConfiguration: gql`
      mutation unassignConfiguration($input: DisplayBulkUnassignConfigurationsInput!) {
        unassignConfiguration(input: $input)
      }
    `,
  },
};
